/**
 * PAY-UNIFIED-02: Bank Reconciliation Workspace
 *
 * NEFT/RTGS/IMPS reconciliation via CSV/XLSX bank statement imports.
 * Backend: /api/finance/bank/* (routes_bank_recon.py)
 *
 * Flow:
 *   1. Create import profile (admin) — maps bank CSV/XLSX columns
 *   2. Upload statement → STAGED (preview + hash-based duplicate detection)
 *   3. Accept import → immutable, auto-match runs
 *   4. Review unmatched / ambiguous lines → manual match or ignore
 *   5. Exception queue → dismiss with supervisor notes
 *
 * Invariants (enforced by backend):
 *   INV-BANK-01: UTR from students NEVER independently confirms payment
 *   INV-BANK-02: Auto-match = UTR + exact amount + account (triple criteria)
 *   INV-BANK-03: Duplicate import detection via SHA-256 file hash
 *   INV-BANK-04: Lines immutable after acceptance
 *   INV-BANK-05: Manual match requires reason (min 5 chars) — audited
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Building,
  Upload,
  RefreshCw,
  Plus,
  AlertTriangle,
  CheckCircle,
  FileText,
  Settings,
  XCircle,
  Eye,
  GitMerge,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => v != null ? `₹${parseFloat(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";

const STATUS_COLORS = {
  UNMATCHED:         "bg-red-100 text-red-800",
  MATCHED:           "bg-emerald-100 text-emerald-800",
  AMBIGUOUS:         "bg-amber-100 text-amber-800",
  MANUALLY_MATCHED:  "bg-blue-100 text-blue-800",
  IGNORED:           "bg-gray-100 text-gray-500",
  OPEN:              "bg-amber-100 text-amber-800",
  PENDING_REVIEW:    "bg-amber-100 text-amber-800",
  RESOLVED:          "bg-emerald-100 text-emerald-800",
  DISMISSED:         "bg-gray-100 text-gray-500",
  STAGED:            "bg-blue-100 text-blue-800",
  ACCEPTED:          "bg-emerald-100 text-emerald-800",
  REJECTED:          "bg-red-100 text-red-800",
};

const StatusBadge = ({ s }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[s] || "bg-gray-100 text-gray-600"}`}>
    {s?.replace(/_/g, " ")}
  </span>
);

// ─── Create Profile Dialog ───────────────────────────────────────────────────
function CreateProfileDialog({ token, onCreated, onClose }) {
  const [form, setForm] = useState({
    profile_name: "",
    bank_name: "",
    date_col: "Txn Date",
    credit_col: "Credit",
    debit_col: "Debit",
    utr_col: "Reference No.",
    description_col: "Description",
    date_format: "DD/MM/YYYY",
  });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.profile_name || !form.bank_name) { toast.error("Profile name and bank name are required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/finance/bank/profiles`, {
        profile_name: form.profile_name,
        bank_name: form.bank_name,
        date_format: form.date_format,
        amount_format: "DEBIT_CREDIT_COLS",
        column_mapping: {
          date: form.date_col,
          description: form.description_col,
          credit: form.credit_col,
          debit: form.debit_col,
          utr_reference: form.utr_col,
        },
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Import profile created");
      onCreated();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Failed to create profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 py-1 max-h-[70vh] overflow-y-auto">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800">
        Map your bank's CSV/XLSX column headers to the system's expected fields. Create one profile per bank format.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Profile Name <span className="text-red-500">*</span></Label>
          <Input data-testid="profile-name-input" value={form.profile_name} onChange={(e) => f("profile_name", e.target.value)} placeholder="HDFC Current 2024" />
        </div>
        <div>
          <Label>Bank Name <span className="text-red-500">*</span></Label>
          <Input data-testid="profile-bank-input" value={form.bank_name} onChange={(e) => f("bank_name", e.target.value)} placeholder="HDFC Bank" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Date Column Header</Label>
          <Input data-testid="profile-date-col" value={form.date_col} onChange={(e) => f("date_col", e.target.value)} />
        </div>
        <div>
          <Label>Date Format</Label>
          <Select value={form.date_format} onValueChange={(v) => f("date_format", v)}>
            <SelectTrigger data-testid="profile-date-format"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "DD-MM-YYYY", "DD MMM YYYY"].map((x) => (
                <SelectItem key={x} value={x}>{x}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Credit Amount Column</Label>
          <Input data-testid="profile-credit-col" value={form.credit_col} onChange={(e) => f("credit_col", e.target.value)} />
        </div>
        <div>
          <Label>Debit Amount Column</Label>
          <Input data-testid="profile-debit-col" value={form.debit_col} onChange={(e) => f("debit_col", e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>UTR / Reference Column</Label>
          <Input data-testid="profile-utr-col" value={form.utr_col} onChange={(e) => f("utr_col", e.target.value)} />
        </div>
        <div>
          <Label>Description / Narration Column</Label>
          <Input data-testid="profile-narration-col" value={form.description_col} onChange={(e) => f("description_col", e.target.value)} />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
        <Button data-testid="profile-submit-btn" onClick={submit} disabled={saving} size="sm">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          {saving ? "Creating…" : "Create Profile"}
        </Button>
      </div>
    </div>
  );
}

// ─── Import Statement Dialog ──────────────────────────────────────────────────
function ImportStatementDialog({ token, profiles, onImported, onClose }) {
  const [profileId, setProfileId] = useState("");
  const [file, setFile] = useState(null);
  const [staged, setStaged] = useState(null);   // response from /imports/upload
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const uploadAndStage = async () => {
    if (!profileId || !file) { toast.error("Select profile and file"); return; }
    setLoading(true);
    setStaged(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("profile_id", profileId);
      const r = await axios.post(`${API}/api/finance/bank/imports/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStaged(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Failed to parse file");
    } finally {
      setLoading(false);
    }
  };

  const acceptImport = async () => {
    if (!staged) return;
    setAccepting(true);
    try {
      const r = await axios.post(
        `${API}/api/finance/bank/imports/${staged.import_id}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Import accepted: ${r.data.total_lines || staged.total_lines} lines. Running auto-match…`);
      // Run auto-match immediately
      try {
        const mr = await axios.post(
          `${API}/api/finance/bank/match/run?import_id=${staged.import_id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Auto-match: ${mr.data.matched_count || 0} matched, ${mr.data.ambiguous_count || 0} ambiguous, ${mr.data.unmatched_count || 0} unmatched.`);
      } catch { /* auto-match failure is non-fatal, user can run it later */ }
      onImported();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Import acceptance failed");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="space-y-4 py-1">
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
        <strong>INV-BANK-04:</strong> Once accepted, statement lines become immutable. Preview the parsed data before accepting.
      </div>

      <div>
        <Label>Import Profile <span className="text-red-500">*</span></Label>
        <Select value={profileId} onValueChange={(v) => { setProfileId(v); setStaged(null); }}>
          <SelectTrigger data-testid="import-profile-select"><SelectValue placeholder="Select bank profile" /></SelectTrigger>
          <SelectContent>
            {profiles.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.profile_name} — {p.bank_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {profiles.length === 0 && (
          <p className="text-xs text-amber-600 mt-1">No profiles found. Create an import profile first (Profiles tab).</p>
        )}
      </div>

      <div>
        <Label>CSV / XLSX File <span className="text-red-500">*</span></Label>
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          data-testid="import-file-input"
          className="block w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700 file:cursor-pointer cursor-pointer mt-1"
          onChange={(e) => { setFile(e.target.files[0] || null); setStaged(null); }}
        />
      </div>

      {staged && (
        <div className="rounded-lg border bg-gray-50 p-3 space-y-2">
          <div className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            Staged — {staged.total_lines} lines parsed
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="bg-white rounded border p-2 text-center">
              <div className="text-gray-500">From</div>
              <div className="font-semibold">{staged.statement_from_date || "—"}</div>
            </div>
            <div className="bg-white rounded border p-2 text-center">
              <div className="text-gray-500">To</div>
              <div className="font-semibold">{staged.statement_to_date || "—"}</div>
            </div>
            <div className="bg-white rounded border p-2 text-center">
              <div className="text-gray-500">Lines</div>
              <div className="font-semibold">{staged.total_lines}</div>
            </div>
          </div>
          {staged.duplicate_warning && (
            <div className="rounded bg-red-50 border border-red-200 p-2 text-xs text-red-700">
              ⚠️ {staged.duplicate_warning}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
        {!staged ? (
          <Button data-testid="import-stage-btn" onClick={uploadAndStage} disabled={loading || !file || !profileId} size="sm">
            {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            {loading ? "Parsing…" : "Stage & Preview"}
          </Button>
        ) : (
          <Button data-testid="import-accept-btn" onClick={acceptImport} disabled={accepting} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {accepting ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
            {accepting ? "Accepting…" : `Accept ${staged.total_lines} Lines`}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Manual Match Dialog ──────────────────────────────────────────────────────
function ManualMatchDialog({ token, line, onMatched, onClose }) {
  const [paymentId, setPaymentId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!paymentId.trim()) { toast.error("Payment ID is required"); return; }
    if (!reason.trim() || reason.trim().length < 5) { toast.error("Reason must be at least 5 characters (INV-BANK-05)"); return; }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/api/finance/bank/lines/${line.id}/match`,
        { payment_id: paymentId, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Manual match applied");
      onMatched();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Match failed");
    } finally {
      setSaving(false);
    }
  };

  const amount = line.amount ? fmt(Math.abs(parseFloat(line.amount))) : "—";

  return (
    <div className="space-y-4 py-1">
      <div className="rounded-lg bg-gray-50 border p-3 text-sm space-y-1">
        <div className="flex justify-between"><span className="text-gray-600">Date</span><span>{line.transaction_date}</span></div>
        <div className="flex justify-between"><span className="text-gray-600">UTR</span><span className="font-mono text-xs">{line.utr_reference || "—"}</span></div>
        <div className="flex justify-between"><span className="text-gray-600">Amount</span><span className="font-semibold">{amount}</span></div>
        <div className="flex justify-between"><span className="text-gray-600">Description</span><span className="max-w-[200px] text-xs text-right">{line.description || "—"}</span></div>
      </div>
      <div>
        <Label>Match to Payment ID <span className="text-red-500">*</span></Label>
        <Input
          data-testid="manual-match-payment-id-input"
          value={paymentId}
          onChange={(e) => setPaymentId(e.target.value)}
          placeholder="UUID of posted NEFT/RTGS/IMPS payment"
        />
      </div>
      <div>
        <Label>Match Reason <span className="text-red-500">*</span></Label>
        <Input
          data-testid="manual-match-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this line matched to this payment? (INV-BANK-05)"
        />
        <p className="text-xs text-gray-500 mt-0.5">Min 5 characters. This is audited.</p>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
        <Button data-testid="manual-match-submit-btn" onClick={submit} disabled={saving} size="sm">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <GitMerge className="h-4 w-4 mr-1" />}
          {saving ? "Matching…" : "Apply Match"}
        </Button>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function BankReconWorkspace() {
  const { user, token } = useAuth();
  const [tab, setTab] = useState("imports");
  const [profiles, setProfiles] = useState([]);
  const [imports, setImports] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [selectedImport, setSelectedImport] = useState(null);
  const [statementLines, setStatementLines] = useState([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [matchTarget, setMatchTarget] = useState(null);
  const [resolveNotes, setResolveNotes] = useState({});

  const isAdmin = ["finance_manager", "institution_admin", "super_admin"].includes(user?.role);
  const isManager = ["finance_manager", "cfo", "institution_admin", "super_admin"].includes(user?.role);

  const loadProfiles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/finance/bank/profiles`, { headers: { Authorization: `Bearer ${token}` } });
      setProfiles(r.data.profiles || []);
    } catch { /* silent */ }
  }, [token]);

  const loadImports = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/finance/bank/imports?limit=30`, { headers: { Authorization: `Bearer ${token}` } });
      setImports(r.data.imports || []);
    } catch { /* silent */ }
  }, [token]);

  const loadExceptions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/finance/bank/exceptions?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      setExceptions(r.data.exceptions || []);
    } catch { /* silent */ }
  }, [token]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([loadProfiles(), loadImports(), loadExceptions()]).finally(() => setLoading(false));
  }, [loadProfiles, loadImports, loadExceptions]);

  useEffect(() => { load(); }, [load]);

  const loadStatementLines = useCallback(async (importId) => {
    setLoadingLines(true);
    try {
      const r = await axios.get(`${API}/api/finance/bank/imports/${importId}/lines?limit=200`, { headers: { Authorization: `Bearer ${token}` } });
      setStatementLines(r.data.lines || []);
    } catch { toast.error("Failed to load statement lines"); } finally {
      setLoadingLines(false);
    }
  }, [token]);

  useEffect(() => {
    if (selectedImport) loadStatementLines(selectedImport.id);
  }, [selectedImport, loadStatementLines]);

  const runAutoMatch = async (importId) => {
    try {
      const r = await axios.post(
        `${API}/api/finance/bank/match/run?import_id=${importId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Auto-match: ${r.data.matched_count || 0} matched, ${r.data.ambiguous_count || 0} ambiguous, ${r.data.unmatched_count || 0} unmatched.`);
      if (selectedImport?.id === importId) loadStatementLines(importId);
      loadExceptions();
      loadImports();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Auto-match failed");
    }
  };

  const dismissException = async (excId) => {
    const note = resolveNotes[excId] || "";
    if (!note.trim()) { toast.error("Resolution note is required"); return; }
    try {
      await axios.patch(
        `${API}/api/finance/bank/exceptions/${excId}/dismiss?reason=${encodeURIComponent(note)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Exception dismissed");
      loadExceptions();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Dismiss failed");
    }
  };

  const openExceptions = exceptions.filter((e) => e.status === "OPEN" || e.status === "PENDING_REVIEW");

  return (
    <div className="min-h-screen bg-gray-50" data-testid="bank-recon-workspace">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Building className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-xl font-semibold text-gray-900">Bank Reconciliation</p>
              <p className="text-sm text-gray-500">NEFT/RTGS/IMPS · CSV/XLSX · Auto-match · Exception queue · PAY-02</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={load} data-testid="bank-recon-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" data-testid="create-profile-btn" onClick={() => setDialog("create-profile")}>
                <Settings className="h-4 w-4 mr-1" />New Profile
              </Button>
            )}
            <Button size="sm" data-testid="import-statement-btn" onClick={() => setDialog("import")}>
              <Upload className="h-4 w-4 mr-1" />Import Statement
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b px-6 py-2">
        <div className="flex gap-6 text-xs text-gray-600">
          <span><strong className="text-gray-900">{imports.filter((i) => i.status === "ACCEPTED").length}</strong> accepted imports</span>
          <span><strong className={openExceptions.length > 0 ? "text-red-700" : "text-gray-900"}>{openExceptions.length}</strong> open exceptions</span>
          <span><strong className="text-gray-900">{profiles.length}</strong> profiles</span>
        </div>
      </div>

      <div className="bg-white border-b px-6">
        <div className="flex gap-0">
          {[
            { key: "imports",    label: "Statement Imports", icon: FileText,      testid: "bank-tab-imports" },
            { key: "lines",      label: "Statement Lines",   icon: FileText,      testid: "bank-tab-lines",  hidden: !selectedImport },
            { key: "exceptions", label: "Exception Queue",   icon: AlertTriangle, testid: "bank-tab-exceptions", badge: openExceptions.length || null },
            { key: "profiles",   label: "Import Profiles",   icon: Settings,      testid: "bank-tab-profiles", hidden: !isAdmin },
          ].filter((t) => !t.hidden).map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              {t.badge ? <span className="rounded-full bg-red-100 text-red-800 text-xs px-1.5 py-0.5 font-semibold">{t.badge}</span> : null}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-4">
        {/* ─── Imports Tab ─────────────────────────────────────────────── */}
        {tab === "imports" && (
          <div data-testid="bank-imports-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-800">Bank Statement Imports</h2>
            </div>
            {imports.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Building className="h-8 w-8 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No bank statements imported</p>
                  <p className="text-sm mt-1">Import a CSV or XLSX bank statement to begin reconciliation.</p>
                  <Button className="mt-4" size="sm" onClick={() => setDialog("import")} data-testid="import-statement-empty-btn">
                    <Upload className="h-4 w-4 mr-1" />Import Statement
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File</TableHead>
                      <TableHead>Profile</TableHead>
                      <TableHead>Date Range</TableHead>
                      <TableHead className="text-right">Lines</TableHead>
                      <TableHead className="text-right">Matched</TableHead>
                      <TableHead className="text-right">Exceptions</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {imports.map((imp) => (
                      <TableRow key={imp.id} data-testid={`import-row-${imp.id}`}>
                        <TableCell className="text-xs font-mono text-gray-600 max-w-[120px] truncate">{imp.file_name || imp.original_filename || "—"}</TableCell>
                        <TableCell className="text-xs">{imp.profile_name || "—"}</TableCell>
                        <TableCell className="text-xs">
                          {imp.statement_from_date || "—"}
                          {imp.statement_to_date && imp.statement_to_date !== imp.statement_from_date ? ` → ${imp.statement_to_date}` : ""}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="rounded-full bg-gray-100 text-gray-700 text-xs px-2">{imp.total_lines}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="rounded-full bg-emerald-100 text-emerald-700 text-xs px-2">{imp.matched_lines || imp.matched_count || 0}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {(imp.exception_lines || imp.unmatched_count || 0) > 0 ? (
                            <span className="rounded-full bg-red-100 text-red-700 text-xs px-2">{imp.exception_lines || imp.unmatched_count}</span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell><StatusBadge s={imp.status} /></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7"
                              data-testid={`view-lines-btn-${imp.id}`}
                              onClick={() => { setSelectedImport(imp); setTab("lines"); }}
                            >
                              <Eye className="h-3 w-3 mr-1" />View
                            </Button>
                            {imp.status === "ACCEPTED" && isManager && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7 border-blue-300 text-blue-700"
                                data-testid={`auto-match-btn-${imp.id}`}
                                onClick={() => runAutoMatch(imp.id)}
                              >
                                <GitMerge className="h-3 w-3 mr-1" />Match
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        )}

        {/* ─── Statement Lines Tab ────────────────────────────────────── */}
        {tab === "lines" && selectedImport && (
          <div data-testid="bank-lines-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Statement Lines — {selectedImport.file_name || "Import"}</h2>
                <p className="text-sm text-gray-500">{selectedImport.total_lines} lines</p>
              </div>
              <div className="flex gap-2">
                {selectedImport.status === "ACCEPTED" && isManager && (
                  <Button size="sm" variant="outline" className="text-xs border-blue-300 text-blue-700" data-testid="run-auto-match-btn" onClick={() => runAutoMatch(selectedImport.id)}>
                    <GitMerge className="h-4 w-4 mr-1" />Run Auto-Match
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setTab("imports")}>← Back</Button>
              </div>
            </div>
            {loadingLines ? (
              <Card><CardContent className="py-12 text-center text-gray-500"><RefreshCw className="h-6 w-6 animate-spin mx-auto mb-3" /><p className="text-sm">Loading…</p></CardContent></Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>UTR Reference</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statementLines.map((line) => (
                      <TableRow key={line.id} data-testid={`stmt-line-${line.id}`}>
                        <TableCell className="text-xs text-gray-400">{line.line_number}</TableCell>
                        <TableCell className="text-sm">{line.transaction_date}</TableCell>
                        <TableCell className="text-xs font-mono text-blue-600">{line.utr_reference || "—"}</TableCell>
                        <TableCell className="text-xs text-gray-600 max-w-[200px] truncate">{line.description || "—"}</TableCell>
                        <TableCell className="text-right text-sm font-medium">{line.amount ? fmt(Math.abs(parseFloat(line.amount))) : "—"}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-medium rounded px-1.5 py-0.5 ${line.credit_debit === "CR" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                            {line.credit_debit || "—"}
                          </span>
                        </TableCell>
                        <TableCell><StatusBadge s={line.status} /></TableCell>
                        <TableCell>
                          {(line.status === "UNMATCHED" || line.status === "AMBIGUOUS") && isManager && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7"
                              data-testid={`manual-match-btn-${line.id}`}
                              onClick={() => { setMatchTarget({ ...line, import_id: selectedImport.id }); setDialog("manual-match"); }}
                            >
                              <GitMerge className="h-3 w-3 mr-1" />Match
                            </Button>
                          )}
                          {(line.status === "MATCHED" || line.status === "MANUALLY_MATCHED") && (
                            <span className="text-xs text-emerald-600">✓ Matched</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        )}

        {/* ─── Exception Queue Tab ──────────────────────────────────── */}
        {tab === "exceptions" && (
          <div data-testid="bank-exceptions-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Exception Queue</h2>
                <p className="text-sm text-gray-500">{openExceptions.length} open exceptions</p>
              </div>
              <Button variant="ghost" size="sm" onClick={loadExceptions} data-testid="refresh-exceptions-btn">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            {exceptions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <CheckCircle className="h-8 w-8 mx-auto mb-3 text-emerald-300" />
                  <p className="font-medium">No exceptions</p>
                  <p className="text-sm mt-1">All bank statement lines have been reconciled.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Resolution Note</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {exceptions.map((exc) => (
                      <TableRow key={exc.id} data-testid={`exception-row-${exc.id}`}>
                        <TableCell>
                          <span className="text-xs font-medium rounded bg-gray-100 px-1.5 py-0.5">{exc.exception_type?.replace(/_/g, " ")}</span>
                        </TableCell>
                        <TableCell className="font-semibold text-sm">{fmt(exc.amount)}</TableCell>
                        <TableCell className="text-xs text-gray-500 max-w-[200px] truncate">{exc.description || exc.narration || "—"}</TableCell>
                        <TableCell><StatusBadge s={exc.status} /></TableCell>
                        <TableCell>
                          {(exc.status === "OPEN" || exc.status === "PENDING_REVIEW") && isManager && (
                            <Input
                              className="h-7 text-xs w-44"
                              placeholder="Enter resolution note…"
                              data-testid={`exc-note-input-${exc.id}`}
                              value={resolveNotes[exc.id] || ""}
                              onChange={(e) => setResolveNotes((p) => ({ ...p, [exc.id]: e.target.value }))}
                            />
                          )}
                          {(exc.status === "DISMISSED" || exc.status === "RESOLVED") && (
                            <span className="text-xs text-gray-500">{exc.resolution_notes || exc.resolution_note || "—"}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {(exc.status === "OPEN" || exc.status === "PENDING_REVIEW") && isManager && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 border-gray-300"
                              data-testid={`dismiss-exc-btn-${exc.id}`}
                              onClick={() => dismissException(exc.id)}
                            >
                              <XCircle className="h-3 w-3 mr-1" />Dismiss
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        )}

        {/* ─── Profiles Tab ──────────────────────────────────────────── */}
        {tab === "profiles" && isAdmin && (
          <div data-testid="bank-profiles-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-800">Import Profiles</h2>
              <Button size="sm" variant="outline" onClick={() => setDialog("create-profile")} data-testid="create-profile-header-btn">
                <Plus className="h-4 w-4 mr-1" />New Profile
              </Button>
            </div>
            {profiles.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Settings className="h-8 w-8 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No profiles</p>
                  <Button className="mt-4" size="sm" onClick={() => setDialog("create-profile")} data-testid="create-profile-empty-btn">
                    <Plus className="h-4 w-4 mr-1" />Create Profile
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {profiles.map((p) => (
                  <Card key={p.id} data-testid={`profile-card-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="font-semibold text-sm mb-1">{p.profile_name}</div>
                      <div className="text-xs text-gray-500 mb-2">{p.bank_name}</div>
                      <div className="space-y-0.5 text-xs text-gray-600">
                        <div className="flex justify-between"><span>Date Format</span><span className="font-mono">{p.date_format}</span></div>
                        <div className="flex justify-between"><span>Amount Format</span><span className="font-mono">{p.amount_format}</span></div>
                        {p.column_mapping && (
                          <div className="flex justify-between"><span>UTR Col</span><span className="font-mono">{p.column_mapping.utr_reference || "—"}</span></div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <Dialog open={dialog === "create-profile"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5" />Create Import Profile</DialogTitle></DialogHeader>
          <CreateProfileDialog token={token} onCreated={() => { loadProfiles(); setDialog(null); }} onClose={() => setDialog(null)} />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "import"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5" />Import Bank Statement</DialogTitle></DialogHeader>
          <ImportStatementDialog
            token={token}
            profiles={profiles}
            onImported={() => { load(); setDialog(null); }}
            onClose={() => setDialog(null)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "manual-match"} onOpenChange={() => { setDialog(null); setMatchTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><GitMerge className="h-5 w-5" />Manual Match</DialogTitle></DialogHeader>
          {matchTarget && (
            <ManualMatchDialog
              token={token}
              line={matchTarget}
              onMatched={() => {
                loadStatementLines(matchTarget.import_id);
                loadExceptions();
                setDialog(null);
                setMatchTarget(null);
              }}
              onClose={() => { setDialog(null); setMatchTarget(null); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
